// Port of splashScreen.dart `_userGate` — the two conditions that must block the
// app outright rather than just send the user to the login screen.
//
// Runs against the CACHED user row, so a boot is instant and works offline; the
// row is kept authoritative by refreshing the user record in the background.
// The master/test account (1234567890 / 1234) is exempt from the device check.

export const MASTER_MOBILE = '1234567890';
export const MASTER_PASS = '1234';

export function isTruthy(v) {
  return v === 1 || v === '1' || v === true;
}

export function isMasterAccount(user) {
  if (!user) return false;
  return String(user.MobileNumber) === MASTER_MOBILE && String(user.Password) === MASTER_PASS;
}

// Why the app is blocked. The two cases are NOT equally final:
//
//   INACTIVE  an administrator disabled the account. Only they can undo it, so
//             the gate is a dead end and must stay one.
//   DEVICE    the account is bound to a different phone. That is the normal
//             situation after someone changes handsets, and it is recoverable
//             by the user — prove ownership of the mobile number by OTP and the
//             account re-binds here. The login screen already does exactly this
//             (Flutter's Checker dialog); the gate simply reaches it later.
export const BLOCK_INACTIVE = 'inactive';
export const BLOCK_DEVICE = 'device';

// Returns { reason, title, message } when the app must be blocked, else null.
//
// `deviceIds` is EVERY identifier this phone may legitimately be known by, not
// one string — see utils/deviceId.acceptableDeviceIds. The sibling apps bind the
// same account under different forms (smartify-automation writes the model,
// SmartLifeIndia-flutter writes Build.ID), so matching only one of them would
// lock the user out of their own phone whenever the other app logged in last.
//
// Accepts a bare string too, so older callers keep working.
export function userGate(user, deviceIds) {
  if (!user) return null;

  if (isTruthy(user.InactiveUser)) {
    return {
      reason: BLOCK_INACTIVE,
      title: 'Account Inactive',
      message: 'Your account has been deactivated and cannot be used.',
    };
  }

  if (!isMasterAccount(user)) {
    const registered = String(user.DeviceName ?? '');
    const ids = (Array.isArray(deviceIds) ? deviceIds : [deviceIds])
      .filter(Boolean)
      .map(String);
    if (registered !== '' && ids.length > 0 && !ids.includes(registered)) {
      return {
        reason: BLOCK_DEVICE,
        title: 'Unauthorised Device',
        message: 'This account is registered on another phone.',
      };
    }
  }

  return null;
}
